import type { Cue } from '@core/model/cue.ts';
import { dubbingScale, visibleDubbingCues } from '@core/dubbing/layout.ts';
import { getTimingPreviewMs } from '@core/dubbing/timingPreview.ts';
import {
  clampTopToFloor,
  clampUnifiedTextSlotTopsToFloor,
  computeBaseline,
  computeShadowingBaseline,
  computeUnifiedTextSlotTops,
  subtitleContentFloor,
  type BaselineLayout,
} from '@core/layout.ts';
import { buildPaintSignature } from '@core/paintSignature.ts';
import { DEFAULT_SETTINGS, type Settings } from '@core/settings/schema.ts';
import { activeCuesAt, effectiveMatchTime } from '@core/timeline.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { VideoController } from '@ports/VideoController.ts';
import { OverlayPainter } from '../adapters/dom/overlayPainter.ts';
import { serviceLoadingText } from '../lib/serviceStatusMessages.ts';
import { resolveSlotLanguage } from '../features/learning/resolveSlotLanguage.ts';
import {
  SubtitleStore,
  type SubtitleSlot,
} from '../state/subtitleStore.ts';

export type AnimationScheduler = {
  request(cb: FrameRequestCallback): number;
  cancel(id: number): void;
};

export type SlotSyncView = {
  cues: Cue[];
  hidden: boolean;
  dualMode: boolean;
  baseline: BaselineLayout;
  hasOtherSlot: boolean;
  signature: string;
  /** Match time used for cue window / ticker (delay-adjusted). */
  currentTimeMs?: number;
  /** Shadowing: playback box mapped into overlay host coords. */
  playbackClientRect?: { left: number; top: number; width: number; height: number };
};

export type SyncSnapshot = {
  main: SlotSyncView | null;
  second: SlotSyncView | null;
  settings: Settings;
  /** Monotonic counter bumped only when a signature changes (for tests). */
  revision: number;
};

export type SubtitleSyncEngineOptions = {
  scheduler?: AnimationScheduler;
  documentRef?: Document;
  onUpdate?: (snapshot: SyncSnapshot) => void;
  /**
   * Host for #mainSub / #secondSub (extension selectableRoot).
   * When set, paint uses OverlayPainter (same algorithm as extension OverlayRenderer).
   */
  getOverlayRoot?: () => HTMLElement | null;
};

const browserScheduler: AnimationScheduler = {
  request: (cb) => requestAnimationFrame(cb),
  cancel: (id) => cancelAnimationFrame(id),
};

function emptySnapshot(settings: Settings, revision: number): SyncSnapshot {
  return { main: null, second: null, settings, revision };
}

/**
 * Port of extension RenderController: rAF sync loop with signature dedupe.
 * Cue DOM + tops are painted imperatively via OverlayPainter (not React layout).
 */
export class SubtitleSyncEngine {
  private settings: Settings = DEFAULT_SETTINGS;
  private frameId: number | undefined;
  private started = false;
  private readonly signatures: Partial<Record<SubtitleSlot, string>> = {};
  private readonly unsubscribers: Array<() => void> = [];
  private readonly scheduler: AnimationScheduler;
  private readonly documentRef: Document;
  private snapshot: SyncSnapshot = emptySnapshot(DEFAULT_SETTINGS, 0);
  private revision = 0;
  private readonly onUpdate: (snapshot: SyncSnapshot) => void;
  private readonly getOverlayRoot: () => HTMLElement | null;
  private painter: OverlayPainter | null = null;
  /**
   * True when the last paint had work but overlay root was missing.
   * Keeps the frame dirty so a later invalidate (host mount) re-paints.
   */
  private paintPending = false;
  /** Frames scheduled while playing (for W-SYNC-04 idle checks). */
  scheduleCount = 0;

  constructor(
    private readonly store: SubtitleStore,
    private readonly video: VideoController,
    private readonly settingsStore: SettingsStore,
    options: SubtitleSyncEngineOptions = {},
  ) {
    this.scheduler = options.scheduler ?? browserScheduler;
    this.documentRef = options.documentRef ?? document;
    this.onUpdate = options.onUpdate ?? (() => undefined);
    this.getOverlayRoot = options.getOverlayRoot ?? (() => null);
  }

  getSnapshot(): SyncSnapshot {
    return this.snapshot;
  }

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    this.settings = await this.settingsStore.getAll();
    this.snapshot = { ...this.snapshot, settings: this.settings };

    this.unsubscribers.push(
      this.store.subscribe(() => this.invalidate()),
      this.settingsStore.subscribe((settings) => {
        this.settings = settings;
        this.invalidate();
      }),
      this.video.on('timeupdate', () => this.invalidate()),
      this.video.on('seeking', () => this.invalidate()),
      this.video.on('seeked', () => this.invalidate()),
      this.video.on('play', () => this.invalidate()),
      this.video.on('resize', () => this.invalidate()),
    );

    const onVisibilityChange = () => {
      if (!this.documentRef.hidden) this.invalidate();
    };
    this.documentRef.addEventListener('visibilitychange', onVisibilityChange);
    this.unsubscribers.push(() =>
      this.documentRef.removeEventListener('visibilitychange', onVisibilityChange),
    );

    this.invalidate();
  }

  stop(): void {
    this.started = false;
    if (this.frameId !== undefined) {
      this.scheduler.cancel(this.frameId);
      this.frameId = undefined;
    }
    this.unsubscribers.splice(0).forEach((unsubscribe) => unsubscribe());
    this.signatures.main = undefined;
    this.signatures.second = undefined;
    this.painter?.clear();
    this.painter = null;
    this.revision += 1;
    this.snapshot = emptySnapshot(this.settings, this.revision);
    this.onUpdate(this.snapshot);
  }

  /** Reset store + clear overlay (navigate / new videoId). */
  resetNavigation(): void {
    this.store.reset();
    this.signatures.main = undefined;
    this.signatures.second = undefined;
    this.paintPending = false;
    this.painter?.clear();
    this.revision += 1;
    this.snapshot = emptySnapshot(
      this.settingsStore.getSnapshot?.() ?? this.settings,
      this.revision,
    );
    this.onUpdate(this.snapshot);
    this.invalidate();
  }

  invalidate(): void {
    this.scheduleFrame();
  }

  private scheduleFrame(): void {
    if (!this.started || this.documentRef.hidden) return;
    if (this.frameId !== undefined) return;
    this.scheduleCount += 1;
    this.frameId = this.scheduler.request(this.tick);
  }

  private hasTrackLoaded(): boolean {
    return (
      this.store.getSlot('main').cues !== undefined
      || this.store.getSlot('second').cues !== undefined
    );
  }

  private readonly tick: FrameRequestCallback = () => {
    this.frameId = undefined;
    if (!this.started) return;

    try {
      if (!this.documentRef.hidden) {
        this.paint();
      }
    } catch (error) {
      console.log(error);
    }

    if (
      this.started
      && !this.documentRef.hidden
      && !this.video.isPaused()
      && this.hasTrackLoaded()
    ) {
      this.scheduleFrame();
    }
  };

  private paint(): void {
    if (this.settingsStore.getSnapshot) {
      this.settings = this.settingsStore.getSnapshot();
    }

    const main = this.computeSlot('main');
    const second = this.computeSlot('second');
    const isShadowing = this.settings.practiceMode === 'shadowing';
    const paintMainChanged = main?.signature !== this.signatures.main
      || (main === null && this.signatures.main !== undefined);
    const paintSecondChanged = second?.signature !== this.signatures.second
      || (second === null && this.signatures.second !== undefined);
    const settingsChanged = this.snapshot.settings !== this.settings;
    // React snapshot dedupe (independent of whether DOM paint succeeded).
    const notifyMainChanged = main?.signature !== this.snapshot.main?.signature
      || Boolean(main) !== Boolean(this.snapshot.main);
    const notifySecondChanged = second?.signature !== this.snapshot.second?.signature
      || Boolean(second) !== Boolean(this.snapshot.second);

    // Shadowing ticker moves every frame with currentTime — never signature-skip.
    if (
      !isShadowing
      && !paintMainChanged
      && !paintSecondChanged
      && !settingsChanged
      && !this.paintPending
      && !notifyMainChanged
      && !notifySecondChanged
    ) {
      return;
    }

    // A0: commit paint signatures only after overlay root actually renders.
    const needsPaint = isShadowing
      || paintMainChanged
      || paintSecondChanged
      || settingsChanged
      || this.paintPending;
    if (needsPaint) {
      const painted = this.paintOverlay(main, second);
      if (painted) {
        if (main) this.signatures.main = main.signature;
        else this.signatures.main = undefined;
        if (second) this.signatures.second = second.signature;
        else this.signatures.second = undefined;
        this.paintPending = false;
      } else {
        this.paintPending = true;
      }
    }

    if (notifyMainChanged || notifySecondChanged || settingsChanged) {
      this.revision += 1;
      this.snapshot = {
        main,
        second,
        settings: this.settings,
        revision: this.revision,
      };
      this.onUpdate(this.snapshot);
    }
  }

  /** Same call shape as extension RenderController → OverlayRenderer.render. */
  private paintOverlay(main: SlotSyncView | null, second: SlotSyncView | null): boolean {
    const root = this.getOverlayRoot();
    if (!root) return false;
    if (!this.painter) this.painter = new OverlayPainter(root);
    else this.painter.setRoot(root);

    if (main) {
      this.painter.render({
        slot: 'main',
        cues: main.cues,
        hasOtherSlot: main.hasOtherSlot,
        hidden: main.hidden,
        dualMode: main.dualMode,
        baseline: main.baseline,
        settings: this.settings,
        lang: resolveSlotLanguage(this.store, 'main'),
        loadingText: serviceLoadingText(),
        currentTimeMs: main.currentTimeMs,
        playbackClientRect: main.playbackClientRect,
      });
    } else {
      this.painter.clear('main');
    }

    if (second) {
      this.painter.render({
        slot: 'second',
        cues: second.cues,
        hasOtherSlot: second.hasOtherSlot,
        hidden: second.hidden,
        dualMode: second.dualMode,
        baseline: second.baseline,
        settings: this.settings,
        lang: resolveSlotLanguage(this.store, 'second'),
        loadingText: serviceLoadingText(),
        currentTimeMs: second.currentTimeMs,
        playbackClientRect: second.playbackClientRect,
      });
    } else {
      this.painter.clear('second');
    }
    return true;
  }

  private computeSlot(slot: SubtitleSlot): SlotSyncView | null {
    const state = this.store.getSlot(slot);
    const other = this.store.getSlot(slot === 'main' ? 'second' : 'main');
    if (state.cues === undefined) return null;

    const cues = state.cues;
    const previewMs = getTimingPreviewMs();
    const videoTimeMs = previewMs ?? this.video.getCurrentTimeMs();
    const currentTime = effectiveMatchTime(videoTimeMs, state.view.delayMs);
    const isShadowing = this.settings.practiceMode === 'shadowing';
    // Mobile is YouTube-only: flush shadowing to the playback box edges.
    const playbackClient = isShadowing ? this.video.getPlaybackClientRect() : undefined;
    const rect = playbackClient
      ? { width: playbackClient.width, height: playbackClient.height }
      : this.video.getViewportRect();

    const slotType = slot === 'main' ? 'mainSubtitle' : 'secondSubtitle';
    const baselineSettings = {
      subtitleSeparation: this.settings.subtitleSeparation,
      subY: this.settings.subY,
      mainSubY: this.settings.mainSubY,
      secondSubY: this.settings.secondSubY,
    };
    const baseline = isShadowing
      ? computeShadowingBaseline(rect, slotType, baselineSettings, {
        flushToPlaybackBox: true,
      })
      : computeBaseline(rect, slotType, baselineSettings);

    let active;
    if (isShadowing) {
      const { lookAheadMs, lookBackMs } = dubbingScale({
        viewWidth: baseline.viewWidth,
        spaceWidth: baseline.spaceWidth,
        currentTimeMs: currentTime,
      });
      active = state.view.priority
        ?? visibleDubbingCues(cues, currentTime, lookAheadMs, lookBackMs);
    } else {
      active = state.view.priority ?? activeCuesAt(cues, currentTime);
    }

    const signature = buildPaintSignature({
      active,
      settings: this.settings,
      hasOther: other.cues !== undefined,
      hidden: state.view.hidden,
      dualMode: state.view.dualMode,
      delayMs: state.view.delayMs,
      rawType: state.rawType,
      viewWidth: rect.width,
      viewHeight: rect.height,
    });

    return {
      cues: active,
      hidden: state.view.hidden,
      dualMode: state.view.dualMode,
      baseline,
      hasOtherSlot: other.cues !== undefined,
      signature,
      currentTimeMs: currentTime,
      playbackClientRect: playbackClient,
    };
  }
}

/** Shared layout helpers (tests / overlay); same math as OverlayPainter / extension. */
export function computeSlotTopPx(opts: {
  slot: SubtitleSlot;
  separation: boolean;
  baseline: BaselineLayout;
  spacing: number;
  totalHeight: number;
  hasOtherSlot: boolean;
  mainHeight: number | null;
  secondHeight: number | null;
}): number {
  const floorY = subtitleContentFloor(opts.baseline.viewBottom);
  if (opts.separation) {
    let top: number;
    if (opts.slot === 'main') {
      top = opts.hasOtherSlot
        ? opts.baseline.top - opts.totalHeight - opts.spacing
        : opts.baseline.top - opts.totalHeight / 2;
    } else {
      top = opts.hasOtherSlot
        ? opts.baseline.top + opts.spacing
        : opts.baseline.top - opts.totalHeight / 2;
    }
    return clampTopToFloor(top, opts.totalHeight, floorY);
  }

  const tops = clampUnifiedTextSlotTopsToFloor(
    computeUnifiedTextSlotTops({
      baselineTop: opts.baseline.top,
      spacing: opts.spacing,
      mainHeight: opts.mainHeight,
      secondHeight: opts.secondHeight,
    }),
    { mainHeight: opts.mainHeight, secondHeight: opts.secondHeight },
    floorY,
  );
  const top = opts.slot === 'main' ? tops.mainTop : tops.secondTop;
  return top ?? opts.baseline.top - opts.totalHeight / 2;
}

export { measureTextBasedHeight } from '../adapters/dom/overlayPainter.ts';
